import type { ProjectTask, TaskActivityLog } from '../types';
import { isGroupTimelineSupplierFocusTitle } from './groupTimeline';
import { unwrapSupplierDisplayTaskId } from './groupTimelineSupplier';

export const TIMELINE_DRIFT_CAUSES = [
  'supplier_delay',
  'supplier_advance',
  'cnc_delay',
  'cnc_advance',
  'weather_delay',
  'internal_delay',
  'internal_advance',
  'other',
] as const;

export type TimelineDriftCause = (typeof TIMELINE_DRIFT_CAUSES)[number];

export const TIMELINE_DRIFT_CAUSE_LABELS: Record<TimelineDriftCause, string> = {
  supplier_delay: 'Supplier delay',
  supplier_advance: 'Supplier advancement',
  cnc_delay: 'CNC delay',
  cnc_advance: 'CNC advancement',
  weather_delay: 'Weather delay',
  internal_delay: 'Internal delay',
  internal_advance: 'Internal advancement',
  other: 'Other',
};

export function isTimelineDriftCause(value: string | undefined): value is TimelineDriftCause {
  return !!value && (TIMELINE_DRIFT_CAUSES as readonly string[]).includes(value);
}

export function isTimelineDriftFocusTitle(title: string | undefined): boolean {
  return (
    isGroupTimelineSupplierFocusTitle(title, 'procurement')
    || isGroupTimelineSupplierFocusTitle(title, 'production')
  );
}

export function isTimelineDriftFocusTask(
  task: Pick<ProjectTask, 'title' | 'parentTaskId'>,
  byId: Map<string, Pick<ProjectTask, 'title' | 'parentTaskId'>>,
): boolean {
  if (isTimelineDriftFocusTitle(task.title)) return true;
  const parent = task.parentTaskId ? byId.get(task.parentTaskId) : undefined;
  return !!parent && isTimelineDriftFocusTitle(parent.title);
}

export interface TimelineDriftGhost {
  taskId: string;
  start: Date;
  end: Date;
  cause: TimelineDriftCause;
  opacity: number;
}

function toMs(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const n = Number(value);
    if (Number.isFinite(n) && n > 1e11) return n;
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  if (value == null) return null;
  const t = new Date(value as Date).getTime();
  return Number.isFinite(t) ? t : null;
}

function createdAtMs(value: unknown): number {
  if (value instanceof Date) {
    const t = value.getTime();
    return Number.isFinite(t) ? t : 0;
  }
  if (value && typeof (value as { toDate?: () => Date }).toDate === 'function') {
    const t = (value as { toDate: () => Date }).toDate().getTime();
    return Number.isFinite(t) ? t : 0;
  }
  return toMs(value) ?? 0;
}

/** `Jun 1, 2026 — Jun 20, 2026` from `buildScheduleMoveActivityEntry`. */
function parseScheduleRangeValue(value: string | undefined): { startMs: number; endMs: number } | null {
  if (!value) return null;
  const parts = value.split(/\s+[—–-]\s+/);
  if (parts.length < 2) return null;
  const startMs = Date.parse(parts[0]);
  const endMs = Date.parse(parts[1]);
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return null;
  return { startMs, endMs };
}

function focusAncestorId(
  taskId: string,
  byId: Map<string, Pick<ProjectTask, 'id' | 'title' | 'parentTaskId'>>,
): string | null {
  const task = byId.get(taskId);
  if (!task) return null;
  if (isTimelineDriftFocusTitle(task.title) && task.id) return task.id;
  const parent = task.parentTaskId ? byId.get(task.parentTaskId) : undefined;
  if (parent?.id && isTimelineDriftFocusTitle(parent.title)) return parent.id;
  return null;
}

function parentRollupRange(
  parentId: string,
  movedTaskId: string,
  movedStartMs: number,
  movedEndMs: number,
  tasks: Pick<ProjectTask, 'id' | 'parentTaskId' | 'startDate' | 'endDate'>[],
): { startMs: number; endMs: number } | null {
  let startMs = Number.POSITIVE_INFINITY;
  let endMs = Number.NEGATIVE_INFINITY;
  tasks.forEach((task) => {
    if (task.parentTaskId !== parentId) return;
    const start = task.id === movedTaskId ? movedStartMs : toMs(task.startDate);
    const end = task.id === movedTaskId ? movedEndMs : toMs(task.endDate);
    if (start == null || end == null) return;
    if (start < startMs) startMs = start;
    if (end > endMs) endMs = end;
  });
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return null;
  return { startMs, endMs };
}

/**
 * One ghost per tagged schedule-move (or paired start/end logs). Only focus tasks
 * with a `delayCause` produce ghosts. Subtask moves also paint the visible parent
 * summary row (Cut Timber Package / Timber Lead Time), which is what people look at.
 */
export function buildTimelineDriftGhosts(
  tasks: Pick<ProjectTask, 'id' | 'title' | 'parentTaskId' | 'startDate' | 'endDate'>[],
  logs: TaskActivityLog[],
): TimelineDriftGhost[] {
  const byId = new Map(tasks.filter((t) => t.id).map((t) => [t.id!, t]));
  const focusIds = new Set(
    tasks.filter((t) => t.id && isTimelineDriftFocusTask(t, byId)).map((t) => t.id!),
  );
  if (focusIds.size === 0) return [];

  const tagged = logs
    .filter((log) => log.taskId && focusIds.has(log.taskId) && isTimelineDriftCause(log.delayCause))
    .slice()
    .sort((a, b) => createdAtMs(a.createdAt) - createdAtMs(b.createdAt));

  const byTask = new Map<string, TaskActivityLog[]>();
  for (const log of tagged) {
    const list = byTask.get(log.taskId) ?? [];
    list.push(log);
    byTask.set(log.taskId, list);
  }

  const ghosts: TimelineDriftGhost[] = [];
  Array.from(byTask.entries()).forEach(([taskId, taskLogs]) => {
    const moves: { startMs: number; endMs: number; cause: TimelineDriftCause }[] = [];
    let i = 0;
    while (i < taskLogs.length) {
      const log = taskLogs[i];
      const next = taskLogs[i + 1];
      const sameMove =
        next
        && next.delayCause === log.delayCause
        && Math.abs(createdAtMs(next.createdAt) - createdAtMs(log.createdAt)) < 2500
        && (log.field === 'startDate' && next.field === 'endDate'
          || log.field === 'endDate' && next.field === 'startDate'
          || log.moveId && next.moveId && log.moveId === next.moveId);

      const parsed = log.field === 'schedule' ? parseScheduleRangeValue(log.oldValue) : null;
      const startMs =
        toMs(log.oldStartMs)
        ?? (log.field === 'startDate' ? toMs(log.oldValue) : null)
        ?? (sameMove && next.field === 'startDate' ? toMs(next.oldValue) : null)
        ?? toMs(next?.oldStartMs)
        ?? parsed?.startMs
        ?? null;
      const endMs =
        toMs(log.oldEndMs)
        ?? (log.field === 'endDate' ? toMs(log.oldValue) : null)
        ?? (sameMove && next.field === 'endDate' ? toMs(next.oldValue) : null)
        ?? toMs(next?.oldEndMs)
        ?? parsed?.endMs
        ?? null;

      if (startMs != null && endMs != null && log.delayCause) {
        moves.push({ startMs, endMs, cause: log.delayCause });
      }
      i += sameMove ? 2 : 1;
    }

    const n = moves.length;
    const parentId = focusAncestorId(taskId, byId);
    moves.forEach((move, idx) => {
      const t = n <= 1 ? 1 : idx / (n - 1);
      const opacity = 0.14 + 0.28 * t;
      ghosts.push({
        taskId,
        start: new Date(move.startMs),
        end: new Date(move.endMs),
        cause: move.cause,
        opacity,
      });
      if (parentId && parentId !== taskId) {
        const rollup = parentRollupRange(parentId, taskId, move.startMs, move.endMs, tasks)
          ?? { startMs: move.startMs, endMs: move.endMs };
        ghosts.push({
          taskId: parentId,
          start: new Date(rollup.startMs),
          end: new Date(rollup.endMs),
          cause: move.cause,
          opacity,
        });
      }
    });
  });
  return ghosts;
}

export type TimelineDriftTask = Pick<
  ProjectTask,
  'id' | 'title' | 'parentTaskId' | 'startDate' | 'endDate'
>;

/** Collapse supplier-split display ids so activity logs match Gantt rows. */
export function canonicalizeTasksForTimelineDrift(tasks: TimelineDriftTask[]): TimelineDriftTask[] {
  const seen = new Set<string>();
  const out: TimelineDriftTask[] = [];
  tasks.forEach((task) => {
    const id = unwrapSupplierDisplayTaskId(task.id) ?? task.id;
    if (!id || seen.has(id)) return;
    seen.add(id);
    out.push({
      ...task,
      id,
      parentTaskId: unwrapSupplierDisplayTaskId(task.parentTaskId) ?? task.parentTaskId,
    });
  });
  return out;
}

/** Paint a real-id ghost on every visible row that unwraps to that task. */
export function mapTimelineDriftGhostsToRows(
  ghosts: TimelineDriftGhost[],
  visibleTasks: Pick<ProjectTask, 'id'>[],
): TimelineDriftGhost[] {
  const displayIdsByReal = new Map<string, string[]>();
  visibleTasks.forEach((task) => {
    if (!task.id) return;
    const real = unwrapSupplierDisplayTaskId(task.id) ?? task.id;
    const list = displayIdsByReal.get(real) ?? [];
    if (list.indexOf(task.id) === -1) list.push(task.id);
    displayIdsByReal.set(real, list);
  });
  const out: TimelineDriftGhost[] = [];
  ghosts.forEach((ghost) => {
    const ids = displayIdsByReal.get(ghost.taskId);
    if (!ids || ids.length === 0) {
      out.push(ghost);
      return;
    }
    ids.forEach((id) => out.push({ ...ghost, taskId: id }));
  });
  return out;
}
